import { Component, ElementRef, OnInit, ViewChild, input, effect, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { isMobileScreen, isSourceFordPass } from '@core/utils';
import { SessionService } from '@agenda-customer-ui/data-access';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-comment-box',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './comment-box.component.html',
  styleUrls: ['./comment-box.component.scss']
})
export class CommentBoxComponent implements OnInit {
  isFocused = input<boolean>(false);
  isServiceRequiringComment = input<boolean>(false);
  placeholder = input.required<string>();

  hasComment = output<boolean>();
  inputFocus = output<boolean>();
  isWithoutComment = output<boolean>();

  @ViewChild('commentbox') observationRef!: ElementRef<HTMLSpanElement>;

  initialComment = '';
  innerText = '';
  onEditComment = false;
  success = false;
  isFordPass = false;
  isMobile = false;

  characterLimit = 3;
  commentLimit = 500;
  disableSaveButton = false;
  hasCommentLengthError = false;
  counter = 0;

  constructor(
    private sessionService: SessionService
  ) {
    effect(() => {
      if (!this.isFocused()) {
        this.onBlur();
      }

      if (this.isFocused()) {
        this.onFocus();

        if (this.isServiceRequiringComment()) {
          this.initialComment = this.#getComment() || '';

          if (this.#hasCommentReachedMinLength(this.characterLimit, this.initialComment)) {
            this.disableSaveButton = false;
            this.hasComment.emit(!!this.initialComment);

            this.onBlur();
            return;
          }

          this.disableSaveButton = true;
          return;
        }
      }
    });
  }

  ngOnInit(): void {
    const sourceParam = this.sessionService.getItem('source');
    this.isFordPass = isSourceFordPass(sourceParam);
    this.isMobile = isMobileScreen();

    this.initialComment = this.#getComment();

    this.counter = this.initialComment?.length || 0;
    if (this.initialComment?.length > 1) {
      this.disableSaveButton = false;
      this.hasComment.emit(true);
    }
  }

  onInputChange(event: Event) {
    const target = event.target as HTMLSpanElement;
    this.innerText = target.innerText || '';

    // Limita a quantidade de caracteres em 500
    if (this.innerText.length >= this.commentLimit) {
      this.innerText = this.innerText.substring(0, this.commentLimit);
      target.innerText = this.innerText;
      const selection = window.getSelection();

      if (selection) {
        const range = document.createRange();
        range.selectNodeContents(target);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }

    this.#setComment(this.innerText);

    // caso o usuário apague todo o texto, por padrão o innerHTML é <br>, então limpamos o campo
    if (target.innerHTML === '<br>') {
      this.#setComment('');
      this.innerText = '';
      this.initialComment = '';
    }

    // Atualiza o counter de caracteres
    this.counter = this.innerText.length;

    if (this.isServiceRequiringComment()) {
      if (!isMobileScreen()) {
        if (!this.#hasCommentReachedMinLength(this.characterLimit, this.innerText)) {
          this.isWithoutComment.emit(true);
          return;
        }
        this.isWithoutComment.emit(false);
      }

      if (this.#hasCommentReachedMinLength(this.characterLimit, this.innerText)) {
        this.hasComment.emit(!!this.innerText);
        this.disableSaveButton = false;
        return;
      }

      this.hasComment.emit(!!this.innerText);
      this.disableSaveButton = true;
      return;
    }

    this.hasComment.emit(!!this.innerText);
    this.disableSaveButton = false;
  }

  #hasCommentReachedMinLength(minLength: number, comment: string): boolean {
    const isValidLength = comment?.length >= minLength;
    this.hasCommentLengthError = !isValidLength;
    return isValidLength;
  }

  onFocus() {
    if (this.observationRef) {
      const element = this.observationRef.nativeElement;
      element.focus();
      this.onEditComment = true;
      this.inputFocus.emit(true);

      // Move o cursor para o final do conteúdo
      if (document.createRange && window.getSelection) {
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(element);
        range.collapse(false); // Colapsa para o final do conteúdo
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }

    if (this.isFordPass) {
      window.scrollBy({ top: 300 });
    }

    if (this.isServiceRequiringComment()) {
      const comment = this.#getComment();
      if (!isMobileScreen()) {
        if (!this.#hasCommentReachedMinLength(this.characterLimit, comment)) {
          this.isWithoutComment.emit(true);
          return;
        }
        this.isWithoutComment.emit(false);
      }
    }
  }

  onBlur() {
    this.onEditComment = false;

    if (this.observationRef?.nativeElement) {
      this.observationRef.nativeElement.blur();
    }

    this.initialComment = this.#getComment();

    if (this.isServiceRequiringComment()) {
      if (!isMobileScreen()) {
        if (!this.#hasCommentReachedMinLength(this.characterLimit, this.initialComment)) {
          this.isWithoutComment.emit(true);
          return;
        }
        this.isWithoutComment.emit(false);
      }

      if (this.#hasCommentReachedMinLength(this.characterLimit, this.initialComment)) {
        this.hasComment.emit(true);
        this.inputFocus.emit(false);
      } else {
        this.inputFocus.emit(false);
        this.hasComment.emit(false);
      }

      return;
    }

    this.hasComment.emit(this.initialComment?.length > 1);
    this.inputFocus.emit(false);
  }

  // TODO: rever quando step 5 estiver pronto com  a nva caixa de comentario pois lá utiliza outra tag da session
  #setComment(details: string): void {
    sessionStorage.setItem('comment', details);
  }

  #getComment(): string {
    return sessionStorage.getItem('comment') as string ;
  }
}

